import { getLocalTimeZone, now } from "@internationalized/date"
import { queryOptions, useMutation } from "@tanstack/react-query"
import { fetchAuthSession } from "aws-amplify/auth"
import {
  GatedRegisterSessionPOSTRequest,
  GatedSessionDetailsPOSTRequest,
} from "ipctg-inflow-types/InFlowGatedAPI"

import api from "@/utilities/api"

import {
  GatedCheckoutError,
  GatedConfirmSetupIntentError,
  GatedEmailConfirmationError,
  GatedGetConfirmationDetailsError,
  GatedPayError,
  GatedPaymentIntentError,
  GatedRegisterSessionError,
  GatedSessionDetailsError,
  GatedSetupIntentError,
  GatedTextConfirmationError,
} from "@/utilities/expectedErrors"

import {
  CHECKOUT_BASE_KEY,
  CONFIRM_PAYMENT_BASE_KEY,
  CONFIRM_SETUP_INTENT_BASE_KEY,
  GATED_BASE_KEY,
  GET_CONFIRMATION_DETAILS_BASE_KEY,
  PAYMENT_INTENT_BASE_KEY,
  PAYMENT_PROCESSOR_BASE_KEY,
  REGISTER_BASE_KEY,
  SESSION_BASE_KEY,
  SESSION_DETAILS_BASE_KEY,
  SETUP_INTENT_BASE_KEY,
} from "./keys"

export const gatedRegisterSessionQueryOptions = (
  args: GatedRegisterSessionPOSTRequest
) => {
  return queryOptions({
    queryKey: [GATED_BASE_KEY, REGISTER_BASE_KEY, args],
    queryFn: async () => {
      const rsp = await api.gated.registerSession.POST({ ...args })

      if ("failReason" in rsp) {
        throw new GatedRegisterSessionError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

export const gatedSessionDetailsQueryOptions = (
  args: GatedSessionDetailsPOSTRequest
) => {
  return queryOptions({
    queryKey: [SESSION_BASE_KEY, SESSION_DETAILS_BASE_KEY, args],
    queryFn: async () => {
      const rsp = await api.gated.sessionDetails.POST(args)
      if ("failReason" in rsp) {
        throw new GatedSessionDetailsError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
  })
}

export const gatedCheckoutQueryOptions = (args: { sessionID: string }) => {
  return queryOptions({
    queryKey: [GATED_BASE_KEY, CHECKOUT_BASE_KEY, args.sessionID],
    queryFn: async () => {
      const rsp = await api.gated.checkout.POST({ sessionID: args.sessionID })

      if ("failReason" in rsp) {
        throw new GatedCheckoutError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    // Note: This causes a double load (cached data + fresh data refetch).
    // This is expected React Query caching behavior. staleTime: Infinity fixes it visually but not sure on the back end leaving it off for the time being
    refetchOnWindowFocus: false,
  })
}

export const gatedSetupIntentQueryOptions = (args: {
  express: boolean | undefined
}) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      GATED_BASE_KEY,
      SETUP_INTENT_BASE_KEY,
      args.express,
    ],
    queryFn: async () => {
      const authSession = await fetchAuthSession()
      const token = authSession.tokens?.idToken?.toString()
      const rsp = await api.gated.setupIntent.POST({
        cognitoIDToken: token,
        isRapidCheckout: !!args.express,
      })
      if ("failReason" in rsp) {
        throw new GatedSetupIntentError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const confirmGatedSetupIntentQueryOptions = (args: {
  sessionID: string
}) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      GATED_BASE_KEY,
      CONFIRM_SETUP_INTENT_BASE_KEY,
      args.sessionID,
    ],
    queryFn: async () => {
      const rsp = await api.gated.confirmSetupIntent.POST({})

      if ("failReason" in rsp) {
        throw new GatedConfirmSetupIntentError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

export const gatedPaymentIntentQueryOptions = (args: {
  sessionID: string
  pamID: string
}) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      GATED_BASE_KEY,
      PAYMENT_INTENT_BASE_KEY,
      args.sessionID,
      args.pamID,
    ],
    queryFn: async () => {
      const authSession = await fetchAuthSession()
      const token = authSession.tokens?.idToken?.toString()
      const rsp = await api.gated.paymentIntent.POST({
        pamID: args.pamID,
        cognitoIDToken: token,
      })
      if ("failReason" in rsp) {
        throw new GatedPaymentIntentError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export const confirmGatedPaymentQueryOptions = (args: {
  stripePaymentIntentID: string | null
  pamID: string
}) => {
  return queryOptions({
    queryKey: [
      PAYMENT_PROCESSOR_BASE_KEY,
      GATED_BASE_KEY,
      CONFIRM_PAYMENT_BASE_KEY,
      args,
    ],
    queryFn: async () => {
      const rsp = await api.gated.pay.POST({
        stripePaymentIntentID: args.stripePaymentIntentID,
        termsAndConditionsOK: now(getLocalTimeZone()).toAbsoluteString(),
        pamID: args.pamID,
      })

      if ("failReason" in rsp) {
        throw new GatedPayError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
  })
}

interface ConfirmationDetailArgs {
  sessionID: string
  confirmationKey: string
}

export const gatedConfirmationDetailsQueryOptions = (
  args: ConfirmationDetailArgs
) => {
  return queryOptions({
    queryKey: [
      SESSION_BASE_KEY,
      args.sessionID,
      GET_CONFIRMATION_DETAILS_BASE_KEY,
      args.confirmationKey,
    ],
    queryFn: async () => {
      const rsp = await api.gated.getConfirmationDetails.POST({
        sessionID: args.sessionID,
        confirmationKey: args.confirmationKey,
      })
      if ("failReason" in rsp) {
        throw new GatedGetConfirmationDetailsError(rsp.failReason)
      }
      return rsp
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

export interface SendConfirmationDetailsVariables {
  method: "phone" | "email"
  overwriteContact: boolean
  input: string
}

export const useSendGatedConfirmationDetails = (
  args: ConfirmationDetailArgs
) => {
  return useMutation({
    mutationFn: async (variables: SendConfirmationDetailsVariables) => {
      let rsp
      if (variables.method === "phone") {
        rsp = await api.gated.textConfirmation.POST({
          sessionID: args.sessionID,
          phone: variables.input,
          overwriteContact: variables.overwriteContact,
          confirmationKey: args.confirmationKey,
        })
        if ("failReason" in rsp) {
          throw new GatedTextConfirmationError(rsp.failReason)
        }
      } else {
        rsp = await api.gated.emailConfirmation.POST({
          sessionID: args.sessionID,
          email: variables.input,
          overwriteContact: variables.overwriteContact,
          confirmationKey: args.confirmationKey,
        })
        if ("failReason" in rsp) {
          throw new GatedEmailConfirmationError(rsp.failReason)
        }
      }
      return rsp
    },
  })
}
